import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';
import {
  CeleryImportState,
  CeleryTaskStatus,
} from '@shared/models/drf-response.models';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Idle } from '@ng-idle/core';
import { Subscription } from 'rxjs';
import { BatchImportService } from '@shared/components/batch-import/batch-import.service';

@Component({
  selector: 'app-loading-progress-bar',
  templateUrl: './loading-progress-bar.component.html',
  styleUrls: ['./loading-progress-bar.component.scss'],
})
export class LoadingProgressBarComponent implements OnDestroy {
  // refresh less than 1-2 secs is too much. it should be more than 5 secs.
  @Input() timeout = 3000;
  @Input() initPercent = 0;
  @Input() title = 'EXPORT.Please wait while the page is loading';

  @Output() taskSuccess = new EventEmitter();
  @Output() taskFailed = new EventEmitter();

  maxFakePercentage: number;
  fakePercent: number;
  fakeIntervalTime: number;
  fakePercentInterval: any;
  randomFallbackPercent = 0;
  lastRandomUpdateAt: number | null = null;
  randomFallbackUpdateMs = 150;
  private highWaterMark = 0;

  sendingTaskInterval: any;
  sendingTaskDetail: CeleryTaskStatus | any;
  taskId: string;

  subscription: Subscription[] = [];

  @ViewChild('progressbarModal', { static: true })
  progressbarModal: ElementRef;

  constructor(
    private apiService: BatchImportService,
    public modalService: NgbModal,
    private idle: Idle,
  ) {}

  showModal(taskId: string): void {
    this.taskId = taskId;
    this.clearState();
    this.subscribe(this.taskId);
    this.modalService.open(this.progressbarModal, {
      backdrop: 'static',
      size: 'md',
      keyboard: false,
      centered: true,
    });
  }

  clearState() {
    this.cancelSendingTask();
    this.maxFakePercentage = this.initPercent;
    this.fakeIntervalTime = this.timeout / this.maxFakePercentage;
    this.fakePercent = 0;
    this.randomFallbackPercent = 0;
    this.lastRandomUpdateAt = null;
    this.highWaterMark = 0;
    this.sendingTaskDetail = {
      _state: CeleryImportState.Pending,
    };
  }

  subscribe(taskId: string): void {
    this.sendingTaskInterval = undefined;
    this.sendingTaskDetail = {
      _state: CeleryImportState.Pending,
    };
    this.fakeInterval();
    this.subscribeSendingTaskStatus(taskId);
  }

  subscribeSendingTaskStatus(taskId: string): void {
    this.sendingTaskInterval = setInterval(() => {
      // send signal to Idle object that user is using website
      //   because report may take a long to time to process.
      this.idle.interrupt();
      if (
        this.sendingTaskDetail == null ||
        (this.sendingTaskDetail._state !==
          CeleryImportState.Success &&
          this.sendingTaskDetail._state !== CeleryImportState.Failure)
      ) {
        const sub = this.apiService
          .getCeleryTaskStatus(taskId)
          .subscribe((res: any) => {
            // update progress
            this.sendingTaskDetail = res;
            // unsubscribe when success or failure
            if (res._state === CeleryImportState.Failure) {
              this.taskFailed.emit(this.sendingTaskDetail);
            } else if (res._state === CeleryImportState.Success) {
              this.fakePercent = 100;
              this.taskSuccess.emit(this.sendingTaskDetail);
            } else {
              if (res.percent === 100) {
                res.percent = 99;
                return;
              }
              const currentPercent = this.percent;
              if (res.percent + this.initPercent > currentPercent) {
                this.maxFakePercentage = Math.min(
                  Math.max(
                    this.maxFakePercentage,
                    currentPercent + this.initPercent,
                  ),
                  100,
                );
                this.fakeInterval();
              } else if (this.fakePercent >= this.maxFakePercentage) {
                this.maxFakePercentage = Math.min(
                  this.fakePercent + this.initPercent,
                  99,
                );
                this.fakeInterval();
              }
            }
          });
        this.subscription.push(sub);
      }
    }, this.timeout);
  }

  cancelSendingTask(): void {
    if (this.sendingTaskInterval) {
      clearInterval(this.sendingTaskInterval);
    }
    if (this.fakePercentInterval) {
      clearInterval(this.fakePercentInterval);
    }
  }

  private randomInt(min: number, max: number): number {
    const minC = Math.ceil(min);
    const maxF = Math.floor(max);
    if (maxF <= minC) return minC;

    const range = maxF - minC + 1;

    const cryptoObj: any =
      (globalThis as any).crypto ?? (crypto as any).webcrypto;

    const arr = new Uint32Array(1);
    cryptoObj.getRandomValues(arr);

    return minC + (arr[0] % range);
  }

  fakeInterval() {
    if (this.fakePercentInterval) {
      clearInterval(this.fakePercentInterval);
    }
    this.fakePercentInterval = setInterval(() => {
      const currentPercent = this.percent;
      if (currentPercent + 1 >= this.maxFakePercentage) {
        clearInterval(this.fakePercentInterval);
      } else {
        this.fakePercent = currentPercent + 1;
      }
    }, this.fakeIntervalTime);
  }

  get percent() {
    const actualPercent =
      this.sendingTaskDetail &&
      typeof this.sendingTaskDetail.percent === 'number'
        ? this.sendingTaskDetail.percent
        : null;

    let basePercent = actualPercent ?? null;

    if (basePercent == null) {
      const now = Date.now();
      if (
        this.lastRandomUpdateAt == null ||
        now - this.lastRandomUpdateAt >= this.randomFallbackUpdateMs
      ) {
        const current = this.randomFallbackPercent || 0;
        const floor = Math.max(current, this.fakePercent || 0);
        const remaining = 99 - floor;
        if (remaining > 0) {
          const maxStep = Math.min(7, remaining);
          const step = Math.max(2, this.randomInt(1, maxStep));
          this.randomFallbackPercent = Math.min(99, floor + step);
        } else {
          this.randomFallbackPercent = 99;
        }
        this.lastRandomUpdateAt = now;
      }
      basePercent = this.randomFallbackPercent || 0;
    }

    const raw = Math.min(
      Math.max(this.fakePercent || 0, basePercent),
      100,
    );
    this.highWaterMark = Math.max(this.highWaterMark, raw);
    return this.highWaterMark;
  }

  ngOnDestroy(): void {
    this.cancelSendingTask();
    if (this.subscription) {
      this.subscription?.forEach((item) => {
        try {
          item.unsubscribe();
        } catch (e) {
          console.error(e);
        }
      });
    }
  }
}
